import type { AuditEntry } from '@contract';
import type { InfiniteData } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { errorCode, errorReason } from '@/components/feedback/api-error';
import { CustomerChip, TransactionChip } from '@/components/money/entity-chips';
import { StatusPill } from '@/components/primitives/status-pill';
import { OpenRecord } from '@/components/queue/queue-parts';
import { RecordSheet } from '@/components/sheet/record-sheet';
import type { SheetBodyProps } from '@/components/sheet/registry';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { callAdmin } from '@/lib/api';
import {
  type AuditSubject,
  actionLabel,
  auditSubject,
  entryPairs,
} from '@/lib/audit-text';
import { formatDateTime } from '@/lib/format';
import { adminQuery, queryClient } from '@/lib/query';
import { useCan, useHas } from '@/lib/staff-context';

type ListShape = { rows: AuditEntry[] };

/** An entry already on screen in a loaded page of the log, if there is one. */
function loadedEntry(id: number): AuditEntry | undefined {
  const lists = queryClient.getQueriesData<InfiniteData<ListShape> | ListShape>(
    { queryKey: ['admin', 'audit.list'] },
  );
  for (const [, data] of lists) {
    if (!data) continue;
    const pages = 'pages' in data ? data.pages : [data];
    for (const page of pages) {
      const hit = page.rows.find((row) => row.id === id);
      if (hit) return hit;
    }
  }
  return undefined;
}

/**
 * One entry of the log. The server has no read-by-id for it, so the entry is
 * taken from a page already loaded, or read as the single row just before the
 * next id. An entry never changes, so it is never read twice.
 */
function useAuditEntry(id: string) {
  const entryId = Number(id);
  return useQuery({
    queryKey: ['admin', 'audit.entry', entryId] as const,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async () => {
      const loaded = loadedEntry(entryId);
      if (loaded) return loaded;
      const page = await callAdmin('audit.list', {
        before_id: entryId + 1,
        limit: 1,
        hide_reads: false,
      });
      const row = page.rows[0];
      if (row?.id !== entryId) {
        throw new Error('This entry is not in the log, or you cannot see it.');
      }
      return row;
    },
  });
}

const OUTCOME_WORDS = {
  ok: { label: 'Done', tone: 'success' },
  refused: { label: 'Refused', tone: 'warning' },
  error: { label: 'Error', tone: 'danger' },
} as const;

export function AuditOutcome({
  outcome,
  size,
}: Readonly<{ outcome: AuditEntry['outcome']; size?: 'sm' | 'md' }>) {
  const words = OUTCOME_WORDS[outcome];
  return <StatusPill tone={words.tone} label={words.label} size={size} />;
}

/** The record an entry is about, as a link that opens it. */
export function AuditSubjectLink({
  subject,
  lite = false,
}: Readonly<{
  subject: NonNullable<AuditSubject>;
  /** In a list: a customer shows as a short id, so no row fetches the customer. */
  lite?: boolean;
}>) {
  switch (subject.type) {
    case 'cust':
      return lite ? (
        <OpenRecord
          sheetRef={{ type: 'cust', id: subject.id }}
          label={`Open customer ${subject.id.slice(0, 8)}`}
          className="font-mono text-xs hover:underline"
        >
          Customer · {subject.id.slice(0, 8)}
        </OpenRecord>
      ) : (
        <CustomerChip id={subject.id} />
      );
    case 'tx':
      return <TransactionChip id={subject.id} />;
    case 'issue':
      return (
        <OpenRecord
          sheetRef={{ type: 'issue', id: subject.id }}
          label={`Open issue report ${subject.id.slice(0, 8)}`}
          className="font-mono text-xs hover:underline"
        >
          Issue · {subject.id.slice(0, 8)}
        </OpenRecord>
      );
    case 'change':
      return (
        <OpenRecord
          sheetRef={{ type: 'change', id: subject.id }}
          label={`Open change request ${subject.id.slice(0, 8)}`}
          className="font-mono text-xs hover:underline"
        >
          Change request · {subject.id.slice(0, 8)}
        </OpenRecord>
      );
  }
}

/** The audit entry sheet (§9.12): what was done, to what, by whom and why, in words. */
export default function AuditSheet({ id }: Readonly<SheetBodyProps>) {
  const query = useAuditEntry(id);
  const seesAll = useHas('audit.all');
  const canListStaff = useCan('staff.list');
  const staff = useQuery({
    ...adminQuery('staff.list', {}),
    enabled: seesAll && canListStaff,
  });
  const sheetRef = { type: 'audit', id } as const;
  const entry = query.data;

  if (!entry) {
    return (
      <RecordSheet
        sheetRef={sheetRef}
        title="Audit entry"
        loading={query.isPending}
        error={
          query.isPending
            ? undefined
            : {
                title: 'Could not load this entry.',
                reason: errorReason(query.error),
                code: errorCode(query.error),
                onRetry: () => void query.refetch(),
              }
        }
      />
    );
  }

  const names = new Map(
    (staff.data ?? []).map((member) => [member.id, member.display_name]),
  );
  const subject = auditSubject(entry);
  const pairs = entryPairs(entry.detail, names);

  return (
    <RecordSheet
      sheetRef={sheetRef}
      title={actionLabel(entry.action)}
      status={<AuditOutcome outcome={entry.outcome} size="md" />}
      meta={
        <>
          <span>{entry.staff_name ?? 'System'}</span>
          <span>{formatDateTime(entry.at)}</span>
        </>
      }
    >
      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">What happened</h3>
        <dl className="grid grid-cols-[8rem_1fr] gap-x-3 gap-y-1.5 text-sm">
          <Row label="Staff">{entry.staff_name ?? 'System'}</Row>
          <Row label="About">
            {subject ? <AuditSubjectLink subject={subject} /> : 'Nothing'}
          </Row>
          <Row label="Time">{formatDateTime(entry.at)}</Row>
          <Row label="Reason">
            {entry.reason ? (
              <span className="whitespace-pre-wrap">{entry.reason}</span>
            ) : (
              <span className="text-muted-foreground">None given</span>
            )}
          </Row>
          {pairs.map((pair) => (
            <Row key={pair.label} label={pair.label}>
              {pair.value}
            </Row>
          ))}
        </dl>
      </section>
      <section className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold">Place in the log</h3>
        <p className="text-sm text-muted-foreground">
          Entry {entry.id.toLocaleString('en-GB')}. Each entry is sealed with
          the one before it, so a change to history shows.
        </p>
      </section>
      <Collapsible className="flex flex-col gap-2">
        <CollapsibleTrigger className="inline-flex h-7 w-fit items-center rounded-sm text-sm font-medium text-muted-foreground hover:text-foreground">
          Technical details
        </CollapsibleTrigger>
        <CollapsibleContent>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-md bg-surface-2 p-3 font-mono text-xs">
            {JSON.stringify(entry, null, 2)}
          </pre>
        </CollapsibleContent>
      </Collapsible>
    </RecordSheet>
  );
}

function Row({
  label,
  children,
}: Readonly<{ label: string; children: React.ReactNode }>) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </>
  );
}
